import { Dispatch } from "react";
import jwtAxios from "../../utils/jwtAxios";
import { AppActions } from "../../types/AppActions";
import { BusinessGroup } from "../../types/models/BusinessGroup";
import { GET_MY_BUSINESS_GROUPS, SELECT_CURRENT_BUSINESS_GROUPS } from "../../types/actions/BusinessGroup.actions";
import { fetchError, showMessage } from "./Common";

type UpdateBeaconGroupPayload = { id: string; name: string; identifier: string; theme: string; title?: string; description?: string; color?: string };

export const getMyBusinessGroups = (callBack: (businessGroups: BusinessGroup[] | undefined) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .get("/api/business/mybusinessgroups")
      .then((data) => {
        if (data.status === 200) {
          const businessGroups: BusinessGroup[] = data.data;
          dispatch({ type: GET_MY_BUSINESS_GROUPS, payload: businessGroups });
          callBack(businessGroups);
        } else {
          dispatch(fetchError("Request failed. Status:" + data.status));
          callBack(undefined);
        }
      })
      .catch((error) => {
        dispatch(fetchError(error.message));
        callBack(undefined);
      });
  };
};

export const createBeaconGroup = (name: string, identifier: string, callBack: (result: boolean) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .post("/api/business/createbeacongroup", { name: name, identifier: identifier })
      .then((data) => {
        if (data.status === 200) {
          dispatch(showMessage("Created Beacon Group " + name));
          callBack(true);
        } else {
          dispatch(fetchError("Request failed. Status:" + data.status));
          callBack(false);
        }
      })
      .catch((error) => {
        if (error.response) {
            console.error("Validation Error Details:", error.response.data);
            dispatch(fetchError(error.response.data.detail || "Unknown validation error"));
        } else if (error.request) {
            console.error("No response received:", error.request);
            dispatch(fetchError("No response from server"));
        } else {
            console.error("Error setting up request:", error.message);
            dispatch(fetchError(error.message));
        }
        callBack(false);
    });
  };
};

export const updateBeaconGroup = (groupId: string, name: string, subdomain: string, theme: string, callBack: (result: boolean) => void) => {
  const payload: UpdateBeaconGroupPayload = { id: groupId, name: name, identifier: subdomain, theme: theme };

  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .post("/api/business/updatebeacongroup", payload)
      .then((data) => {
        if (data.status === 200) {
          callBack(true);
        } else {
          dispatch(fetchError("Request failed. Status:" + data.status));
          callBack(false);
        }
      })
      .catch((error) => {
        console.log("error", error);
        if (error.response && error.response.data && error.response.data.detail) {
          dispatch(fetchError(error.response.data.detail));
        } else {
          dispatch(fetchError(error.message));
        }
        callBack(false);
      });
  };
};

export const updateBeaconGroupSettings = (
  groupId: string,
  name: string,
  subdomain: string,
  theme: string,
  title: string,
  description: string,
  color: string,
  callBack: (result: boolean) => void
) => {
  const payload: UpdateBeaconGroupPayload = {
    id: groupId,
    name: name,
    identifier: subdomain,
    theme: theme,
    title: title,
    description: description,
    color: color.replace("#", ""),
  };

  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .post("/api/business/updatebeacongroup", payload)
      .then((data) => {
        console.log("data", data);
        if (data.status === 200) {
          callBack(true);
        } else {
          dispatch(fetchError("Request failed. Status:" + data.status));
          callBack(false);
        }
      })
      .catch((error) => {
        console.log("error", error);
        if (error.response && error.response.data && error.response.data.detail) {
          dispatch(fetchError(error.response.data.detail));
        } else {
          dispatch(fetchError(error.message));
        }
        callBack(false);
      });
  };
};

export const uploadSettingsAsset = (group_id: string, formData: FormData, callback: (result: boolean) => void) => {
  return (dispatch: Dispatch<AppActions>) => {

    const config = {
      headers: {
          "Content-Type": "multipart/form-data"
      }
    };
    
    const url = "/api/business/uploadsettingsasset?beacongroupid=" + group_id;

    jwtAxios
      .post(url, formData, config)
      .then((data) => {
        if (data.status === 200) {
          callback(true);
        } else {
          dispatch(fetchError("Request failed. Status: " + data.status));
          callback(false);
        }
      })
      .catch((error) => {
        dispatch(fetchError(
          error.response.data.detail
            ? error.response.data.detail
            : error.message));
        callback(false);
      });
  };
};

export const deleteSettingsAsset = (group_id: string, callBack: (result: boolean) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios.post('/api/business/deletesettingsasset?beacongroupid=' + group_id) 
      .then((data) => {
        if (data.status === 200) {
          callBack(true);
        } else {
          dispatch(fetchError("Request failed. Status: " + data.status));
          callBack(false);
        }
      })
      .catch((error) => {
        dispatch(fetchError(
          error.response.data.detail
            ? error.response.data.detail
            : error.message));
        callBack(false);
      });
  };
};

export const setCurrentBusinessGroup = (businessGroup: BusinessGroup | null) => {
  return (dispatch: Dispatch<AppActions>) => {
    dispatch({ type: SELECT_CURRENT_BUSINESS_GROUPS, payload: businessGroup });
  };
};

export const deleteBusinessGroup = (groupId: string, callBack: (result: boolean) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .post("/api/business/deletebeacongroup", { id: groupId })
      .then((data) => {
        if (data.status === 200) {
          dispatch(showMessage("The beacon group has been deleted"));
          callBack(true);
        } else {
          dispatch(fetchError("Request failed. Status:" + data.status));
          callBack(false);
        }
      })
      .catch((error) => {
        dispatch(fetchError(
          error.response.data.detail
            ? error.response.data.detail
            : error.message));
        callBack(false);
      });
  };
};

export const clearBeaconGroups = () => {
  return (dispatch: Dispatch<AppActions>) => {
    dispatch({ type: GET_MY_BUSINESS_GROUPS, payload: [] });
    dispatch({ type: SELECT_CURRENT_BUSINESS_GROUPS, payload: null });
  };
};

export const archiveBeaconGroup = (group_id: string, callBack: (result: boolean) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
    .post('/api/business/archivegroup?group_id=' + group_id)
      .then((data) => {
        if (data.status === 200) {
          dispatch(showMessage("The beacon group has been archived"));
          callBack(true);
        } else {
          dispatch(fetchError("Request failed. Status:" + data.status));
          callBack(false);
        }
      })
      .catch((error) => {
        dispatch(fetchError(
          error.response.data.detail
            ? error.response.data.detail
            : error.message));
        callBack(false);
      });
  };
};

export const duplicateBeaconGroup = (group_id: string, name: string, callBack: (result: boolean) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
    .post('/api/business/duplicategroup?group_id=' + group_id + "&name=" + name)
      .then((data) => {
        if (data.status === 200) {
          dispatch(showMessage("The beacon group has been duplicated"));
          callBack(true);
        } else {
          dispatch(fetchError("Request failed. Status:" + data.status));
          callBack(false);
        }
      })
      .catch((error) => {
        dispatch(fetchError(
          error.response.data.detail
            ? error.response.data.detail
            : error.message));
        callBack(false);
      });
  };
};

export const shareBeaconGroup = (group_id: string, email: string, accessLevel: string, callBack: (result: boolean, code?: string) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
    .post('/api/business/creategroupinvite?group_id=' + group_id + "&email=" + email + "&accessLevel=" + accessLevel)
      .then((data) => {
        if (data.status === 200) {
          dispatch(showMessage("A share invite link has been created"));
          callBack(true, data.data.invitecode);
        } else {
          dispatch(fetchError("Request failed. Status:" + data.status));
          callBack(false);
        }
      })
      .catch((error) => {
        dispatch(fetchError(
          error.response.data.detail
            ? error.response.data.detail
            : error.message));
        callBack(false);
      });
  };
};

export const acceptBeaconGroup = (code: string, callBack: (result: boolean, beacongroupid?: string) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
    .post('/api/business/acceptgroupinvite?code=' + code)
      .then((data) => {
        if (data.status === 200) {
          dispatch(showMessage("Accepted beacon group invite"));
          callBack(true, data.data.beacongroupid);
        } else {
          dispatch(fetchError("Request failed. Status:" + data.status));
          callBack(false);
        }
      })
      .catch((error) => {
        dispatch(fetchError(
          error.response.data.detail
            ? error.response.data.detail
            : error.message));
        callBack(false);
      });
  };
};

export const getWebARAnalytics = (group_id: string, callBack: (analytics: []) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .get("/api/business/webaranalytics?group_id=" + group_id)
      .then((data) => {
        if (data.status === 200) {
          callBack(data.data);
        } else {
          dispatch(fetchError("Request failed. Status:" + data.status));
          callBack([]);
        }
      })
      .catch((error) => {
        dispatch(fetchError(
          error.response.data.detail
            ? error.response.data.detail
            : error.message));
        callBack([]);
      });
  };
};